// ContentAwareG2Gate — the Phase-2 review surface for the Observer's
// Constraint Log + Document Map.
//
// The previous version asked the architect to Approve / Edit / Reject
// three abstract questions ("Is the constraint count consistent…",
// "Have you reviewed every flagged constraint…", "Is the Document
// Map's classification accurate?") with no inline visibility into the
// underlying artifacts. If the Observer produced zero constraints
// (e.g. T3 wasn't uploaded), the constraint table simply didn't render
// and the architect had nothing on screen to evaluate.
//
// This rewrite:
//   1. Always renders an Observer-status banner (artifact count,
//      flagged-for-review count, empty-state explanation).
//   2. Renders the Document Map markdown inline so the architect can
//      actually see what they're being asked to approve.
//   3. Renders a clear empty-state for the Constraint Log when zero
//      constraints were extracted.
//   4. Pairs every decision with a small, specific context block so
//      the question is grounded in a visible piece of evidence.

import type { ReactElement, ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Edit3,
  FileText,
  Inbox,
  Info,
  Link2,
  Loader2,
  Map as MapIcon,
  Pickaxe,
  RefreshCw,
  ShieldAlert,
  X,
} from 'lucide-react'

import { api, ApiError, projectFileUrl } from '@/lib/api'
import { stripFrontmatter } from '@/lib/markdown'
import { decisionLabel } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import type { GateDecisionRecord, GateDecisionStatus, Project } from '@/types'
import {
  gateDraftKey,
  reconcileDraftWithGate,
  useGateDraft,
  useGateDraftStore,
} from '@/stores/gateDraftStore'
import {
  type ConstraintResolution,
  type ConstraintStatus,
  constraintResolutionKey,
  useConstraintResolutionStore,
  useConstraintResolutionsForRun,
} from '@/stores/constraintResolutionStore'
import { AutoApproveAllButton } from '@/components/command-center/AutoApproveAllButton'

// The Observer's category vocabulary (mirrors
// ``ConstraintCategory`` in ``src/ai_alm/models/artifact.py``). Used by
// the per-constraint Recategorize picker so the architect can only
// pick a category Phase 3 actually understands.
const CONSTRAINT_CATEGORIES = [
  'integration',
  'sla',
  'compliance',
  'data',
  'security',
  'ux',
  'performance',
] as const

// Decision-id prefix the backend uses for charter-mined candidate
// review rows (Q4 — see ``build_g2_content`` in
// ``src/ai_alm/orchestrator/gate_emitter.py``). Matching this prefix
// lets us split the otherwise-flat decisions list into the legacy
// abstract-question section + a dedicated "Charter-mined candidates"
// section that renders explicit (A) accept-as-is / (B) edit-and-accept
// / (C) reject buttons mapped onto approved/edited/rejected.
const PREFIX_MINED = 'G2-MINED-'

/** Structured payload the backend attaches to every G2-MINED-* decision. */
interface MinedCandidatePayload {
  kind: 'charter_mined_candidate'
  candidate_id: string
  text: string
  classification: string
  regulatory_tag?: string | null
  confidence?: number | null
  /** Primary source — the (artifact, line) of the cleanest occurrence. */
  source_artifact?: string | null
  source_line_range?: number[] | null
  /** Every (artifact, line, candidate_id) the miner produced for this
   *  normalised text. The backend collapses verbatim duplicates (e.g.
   *  the project charter quoting the problem statement) into one
   *  decision and surfaces every occurrence here so the architect can
   *  still cross-reference the original locations. ``sources[0]`` is
   *  always the primary. */
  sources?: Array<{
    suggested_id?: string
    text?: string
    confidence?: number
    source_artifact?: string
    source_line_range?: number[]
    classification?: string
    regulatory_tag?: string | null
  }>
  /** Number of *additional* occurrences beyond the primary source.
   *  Driven straight off the backend so the UI doesn't have to
   *  infer it from ``sources.length``. */
  duplicate_count?: number
  options: Array<{
    id: string
    label: string
    summary: string
    maps_to_status: GateDecisionStatus
    needs_text?: boolean
  }>
}

function isMinedDecision(d: GateDecisionRecord): boolean {
  return d.id.startsWith(PREFIX_MINED)
}

function getMinedPayload(
  d: GateDecisionRecord,
): MinedCandidatePayload | null {
  const p = d.payload as Partial<MinedCandidatePayload> | null | undefined
  if (!p || p.kind !== 'charter_mined_candidate') return null
  return p as MinedCandidatePayload
}


export interface ContentAwareG2Props {
  project: Project
  runId: number
  onApproved?: () => void
  /**
   * When true, the architect still has a Compliance Triage gate (G2.5)
   * to review before Phase 3 can run. The "Approved → next phase" CTA
   * therefore navigates to G2.5 instead of kicking off Phase 3
   * immediately. Without this, the swarm consumes an un-triaged
   * compliance stub pack and the architect never sees G2.5.
   *
   * Defaults to false (legacy behaviour) so non-regulated projects
   * keep the one-click "Run Phase 3" affordance.
   */
  nextIsGate25?: boolean
}


interface ConstraintRecord {
  id: string
  source_artifact: string
  extraction_method: string
  excerpt: string
  category: string
  confidence: number
  requires_human_review: boolean
  linked_requirements?: string[]

  // Option C (2026-05-19): per-regulation traceability. When a charter-
  // mined constraint's excerpt mentions multiple regulatory standards
  // (e.g. "satisfy ISO 27001, SOC 2, PCI-DSS"), the backend collapses
  // the per-tag candidates into one Constraint row and records every
  // tag here so the chip list can render them separately. Empty for
  // Observer-extracted constraints (the Observer doesn't tag) and for
  // legacy pre-Option-C logs.
  regulatory_tags?: string[]

  // Confidence Hardening v1 (plan/AI_ALM_Constraint_Confidence_Plan.md §4).
  // All optional + nullable so a constraint-log.json from a project that
  // ran Phase 2 with the flags off still renders without `null is not a
  // function` mishaps. `null` means "the corresponding step did not run
  // for this row" (vision-only, source unreachable, flag off, …).
  grounded?: boolean | null
  grounding_score?: number | null
  grounding_method?: 'exact' | 'fuzzy_token_set' | 'skipped' | null
  verifier_passed?: boolean | null
  verifier_model?: string | null
  verifier_reasoning?: string | null
  verifier_category_suggestion?: string | null

  // Confidence Hardening v2 (architect feedback 2026-05-09): single
  // reasoned verdict that collapses self-score / grounder / verifier
  // into one outcome. Optional + nullable so older constraint-log.json
  // files (pre-v2) still validate. ``null`` means the adjudicator did
  // not run for this row.
  review_verdict?: 'auto_approve' | 'needs_review' | 'uncertain' | null
  review_primary_reason?:
    | 'low_self_score'
    | 'ungrounded'
    | 'borderline_grounding'
    | 'category_mismatch'
    | 'verifier_unavailable'
    | 'parse_error'
    // PR 2 — see plan/AI_ALM_Constraint_Confidence_PR2_Plan.md
    | 'stale_link'
    | 'superseded_by_decision'
    | 'unclear_liveness'
    // PR 3 — see plan/AI_ALM_Constraint_Confidence_PR3_Plan.md
    | 'cross_llm_disagreement'
    | null
    | string  // forward-compat

  review_reasons?: Array<{
    signal: string
    ok: boolean | null
    score: number | null
    note: string
    errored?: boolean
    // PR 2 — when ``signal=="liveness"`` the reason carries the
    // architect decision_id the verifier blamed. The chip uses this
    // to deep-link into the gate decision panel.
    decision_id?: string
  }>

  // PR 2 — Tier 2.6: deterministic linked_requirements existence
  // check produced by ``link_existence_checker``. ``null`` means the
  // checker did not run for this row (no linked_requirements, or the
  // ``enable_link_existence_check`` flag was off).
  link_check?: {
    checked: string[]
    resolved: string[]
    missing: string[]
    unknown: string[]
  } | null

  // PR 2 — Tier 2.5: when the verifier judged this constraint
  // superseded by a prior architect decision, this is the
  // decision_id it cited. Surfaced inline so the chip can deep-link
  // into the gate decision card.
  superseded_by?: string | null
}


/** Render-friendly label for each ``review_primary_reason`` token.
 *
 * Centralised here so the chip rendered next to a needs-review row
 * stays consistent with the verdict tooltip rendered on the verdict
 * stat tile. Forward-compat: unknown reason tokens fall through to
 * a humanised version of the raw token.
 */
const REVIEW_REASON_LABELS: Record<string, string> = {
  low_self_score: 'Low self-score',
  ungrounded: 'Ungrounded',
  borderline_grounding: 'Borderline grounding',
  category_mismatch: 'Category mismatch',
  verifier_unavailable: 'Verifier unavailable',
  parse_error: 'Parse error',
  // PR 2 — see plan/AI_ALM_Constraint_Confidence_PR2_Plan.md
  stale_link: 'Linked requirement missing',
  superseded_by_decision: 'Superseded by decision',
  unclear_liveness: 'Liveness unclear',
  // PR 3 — see plan/AI_ALM_Constraint_Confidence_PR3_Plan.md
  cross_llm_disagreement: 'Cross-LLM disagreement',
}



function reviewReasonLabel(reason: string | null | undefined): string {
  if (!reason) return ''
  return (
    REVIEW_REASON_LABELS[reason] ??
    reason.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
  )
}

/** A constraint source the pipeline authored itself (charter / intent
 *  map / problem statement) — i.e. NOT an uploaded T3 artifact. Used to
 *  describe where constraints came from accurately on a words-only
 *  project (constraints charter-mined, nothing uploaded). */
function isCharterSource(source: string): boolean {
  return /^phase\d\//.test(source) || /problem.?statement/i.test(source)
}

/** Readable label for a constraint's source_artifact path. */
function humanizeSource(source: string): string {
  const lower = source.toLowerCase()
  if (lower.includes('intent-map')) return 'Intent Map'
  if (lower.includes('project-charter') || lower.includes('charter'))
    return 'Project Charter'
  if (lower.includes('glossary')) return 'Glossary'
  if (/problem.?statement/i.test(source)) return 'Problem statement'
  return source.replace(/^phase\d\//, '').replace(/\.[a-z0-9]+$/i, '')
}


export function ContentAwareG2Gate({
  project,
  runId,
  onApproved,
  nextIsGate25 = false,
}: ContentAwareG2Props) {

  const gateNumber = 2
  const draftKey = gateDraftKey(project.id, runId, gateNumber)
  const decisions = useGateDraft(draftKey)
  const setDecisionsDraft = useGateDraftStore((s) => s.setDraft)
  const clearDecisionsDraft = useGateDraftStore((s) => s.clearDraft)
  const setDecisions = (next: GateDecisionRecord[]) =>
    setDecisionsDraft(draftKey, next)
  // Per-constraint resolutions (Keep / Recategorize / Reject) live in
  // their own zustand store so navigating away/back keeps the picks and
  // so the rollup logic below can derive G2-D2's status + answer text
  // without owning the data model itself.
  const setResolution = useConstraintResolutionStore((s) => s.setResolution)
  const clearResolution = useConstraintResolutionStore((s) => s.clearResolution)
  const clearResolutionsForRun = useConstraintResolutionStore((s) => s.clearForRun)
  const resolutions = useConstraintResolutionsForRun(project.id, runId)
  const [constraints, setConstraints] = useState<ConstraintRecord[]>([])
  const [documentMap, setDocumentMap] = useState('')
  const [constraintsLoaded, setConstraintsLoaded] = useState(false)
  const [documentMapLoaded, setDocumentMapLoaded] = useState(false)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const gate = useQuery({
    queryKey: ['gate', project.id, runId, gateNumber],
    queryFn: () => api.getGate(project.id, runId, gateNumber),
    retry: false,
  })

  // Load constraint log and document map
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const logRes = await fetch(
          projectFileUrl(project.id, `runs/run-${String(runId).padStart(3, '0')}/phase2/constraint-log.json`)
        )
        if (logRes.ok && !cancelled) {
          const json = await logRes.json()
          if (Array.isArray(json)) setConstraints(json)
        }
      } catch { /* ignore */ }
      finally { if (!cancelled) setConstraintsLoaded(true) }

      try {
        const docRes = await fetch(
          projectFileUrl(project.id, `runs/run-${String(runId).padStart(3, '0')}/phase2/document-map.md`)
        )
        if (docRes.ok && !cancelled) setDocumentMap(await docRes.text())
      } catch { /* ignore */ }
      finally { if (!cancelled) setDocumentMapLoaded(true) }
    }
    load()
    return () => { cancelled = true }
  }, [project.id, runId])

  // Seed decisions from the freshly-fetched gate payload, reconciling
  // any persisted localStorage draft against the server's decision-id
  // set first. See ``reconcileDraftWithGate`` for the stale-draft
  // hygiene that keeps a deleted-and-recreated project from inheriting
  // the prior project's Approve/Edit/Reject picks.
  useEffect(() => {
    if (!gate.data) return
    reconcileDraftWithGate(draftKey, gate.data)
  }, [gate.data, draftKey])

  const emit = useMutation({
    mutationFn: () => api.createGate(project.id, runId, gateNumber),
    onSuccess: () => {
      clearDecisionsDraft(draftKey)
      // Re-emit means Phase 2 was just regenerated, so the previous
      // per-constraint resolutions point at stale ids. Drop them so the
      // architect starts the review fresh against the new log.
      clearResolutionsForRun(project.id, runId)
      gate.refetch()
      toast({ variant: 'success', title: 'G2 re-emitted' })
    },
    onError: (err) => toast({ variant: 'destructive', title: 'Re-emit failed', description: (err as Error).message }),
  })

  const submit = useMutation({
    mutationFn: () => api.submitGateDecisions(project.id, runId, gateNumber, decisions),
    onSuccess: (g) => {
      gate.refetch()
      if (g.status === 'approved') {
        toast({ variant: 'success', title: 'G2 approved', description: 'Ready to run Phase 3.' })
      }
    },
    onError: (err) => toast({ variant: 'destructive', title: 'Could not submit decisions', description: (err as Error).message }),
  })

  const allDecided = decisions.length > 0 && decisions.every((d) => d.status !== 'pending')
  const gateCleared = gate.data?.status === 'approved' || gate.data?.status === 'modified'

  const updateDecision = (id: string, patch: Partial<GateDecisionRecord>) =>
    setDecisions(decisions.map((d) => (d.id === id ? { ...d, ...patch } : d)))

  const missing = gate.error instanceof ApiError && gate.error.status === 404

  const sortedConstraints = useMemo(() => {
    // Sort order, top to bottom:
    //   1. Flagged + still unreviewed (the architect's actual to-do list)
    //   2. Flagged + already resolved
    //   3. Unflagged
    //   Within each bucket, lowest confidence first.
    return [...constraints].sort((a, b) => {
      const aFlagged = a.requires_human_review
      const bFlagged = b.requires_human_review
      if (aFlagged !== bFlagged) return aFlagged ? -1 : 1
      if (aFlagged && bFlagged) {
        const aResolved = (resolutions.get(a.id)?.status ?? 'unreviewed') !== 'unreviewed'
        const bResolved = (resolutions.get(b.id)?.status ?? 'unreviewed') !== 'unreviewed'
        if (aResolved !== bResolved) return aResolved ? 1 : -1
      }
      return a.confidence - b.confidence
    })
  }, [constraints, resolutions])

  const flaggedCount = useMemo(
    () => constraints.filter((c) => c.requires_human_review).length,
    [constraints],
  )

  // Confidence Hardening v1 sub-signal counts. We compute these from
  // the on-disk `constraint-log.json` rather than the gate's WS payload
  // so the numbers are always exact even after the gate's been
  // hand-edited via the Re-emit flow.
  const lowSelfScoreCount = useMemo(
    () => constraints.filter((c) => c.confidence < 0.7).length,
    [constraints],
  )
  const ungroundedCount = useMemo(
    () => constraints.filter((c) => c.grounded === false).length,
    [constraints],
  )
  const verifierDisagreementCount = useMemo(
    () => constraints.filter((c) => c.verifier_passed === false).length,
    [constraints],
  )
  const evidenceEnabled = useMemo(
    () =>
      constraints.some(
        (c) =>
          c.grounded !== undefined && c.grounded !== null
            || c.verifier_passed !== undefined && c.verifier_passed !== null,
      ),
    [constraints],
  )

  // Confidence Hardening v2 — Adjudicator verdict roll-up.
  //
  // The frontend prefers ``review_verdict`` when present and falls
  // back to deriving counts from ``requires_human_review`` for older
  // pre-v2 logs. ``verdictsAvailable`` flips the new
  // auto-approve/needs-review/uncertain tile group on; off, we keep
  // rendering the v1 "flagged for review" tile alone.
  const verdictsAvailable = useMemo(
    () => constraints.some((c) => !!c.review_verdict),
    [constraints],
  )
  const verdictCounts = useMemo(() => {
    let auto = 0
    let needsReview = 0
    let uncertain = 0
    const reasonBuckets: Record<string, number> = {}
    for (const c of constraints) {
      if (c.review_verdict === 'auto_approve') auto += 1
      else if (c.review_verdict === 'needs_review') needsReview += 1
      else if (c.review_verdict === 'uncertain') uncertain += 1
      const r = c.review_primary_reason
      if (typeof r === 'string' && r) {
        reasonBuckets[r] = (reasonBuckets[r] ?? 0) + 1
      }
    }
    return { auto, needsReview, uncertain, reasonBuckets }
  }, [constraints])


  const flaggedIds = useMemo(
    () => constraints.filter((c) => c.requires_human_review).map((c) => c.id),
    [constraints],
  )

  // ----------------------------------------------------------------- //
  // Per-constraint resolution rollup. The architect's Keep / Recategorize
  // / Reject picks live in `resolutions` (zustand). We compute a simple
  // counts object + a "all flagged are resolved" flag, and use them to:
  //   - drive the rollup banner above the decisions section
  //   - auto-fill G2-D2's status + answer text so the audit trail
  //     captures *which* constraints got which treatment instead of one
  //     prose blob covering ten different findings
  // ----------------------------------------------------------------- //
  const resolutionCounts = useMemo(() => {
    let kept = 0
    let recategorized = 0
    let rejected = 0
    let reviewed = 0
    for (const id of flaggedIds) {
      const r = resolutions.get(id)
      if (!r || r.status === 'unreviewed') continue
      reviewed += 1
      if (r.status === 'kept') kept += 1
      else if (r.status === 'recategorized') recategorized += 1
      else if (r.status === 'rejected') rejected += 1
    }
    return {
      kept,
      recategorized,
      rejected,
      reviewed,
      total: flaggedIds.length,
      remaining: flaggedIds.length - reviewed,
    }
  }, [flaggedIds, resolutions])

  const allFlaggedResolved =
    flaggedIds.length > 0 && resolutionCounts.remaining === 0

  // Compose the human-readable G2-D2 rollup line for both the on-screen
  // banner and the auto-filled `answer` field. Keeping the format stable
  // (one bullet per touched constraint) means downstream tooling can
  // grep the gate decision for "C-003 → recategorized: ux" without the
  // architect having to write that prose by hand.
  const rolledUpAnswerLines = useMemo(() => {
    const lines: string[] = []
    for (const c of constraints) {
      if (!c.requires_human_review) continue
      const r = resolutions.get(c.id)
      if (!r || r.status === 'unreviewed') continue
      let line = ''
      if (r.status === 'kept') {
        line = `${c.id} → kept as ${c.category}`
      } else if (r.status === 'recategorized') {
        line = `${c.id} → recategorize ${c.category} → ${r.newCategory ?? '?'}`
      } else if (r.status === 'rejected') {
        line = `${c.id} → reject (not a real constraint)`
      }
      if (r.note && r.note.trim()) {
        line += ` — ${r.note.trim()}`
      }
      lines.push(line)
    }
    return lines
  }, [constraints, resolutions])

  // Auto-sync the per-constraint rollup into the G2-D2 decision so the
  // existing backend submit path (which reads `decisions[]`) captures
  // the architect's per-row picks without any schema change. We mirror
  // the rollup status into D2's status:
  //   - all flagged kept            → approved (no edits)
  //   - any recategorize / reject   → edited (with structured note)
  //   - none touched & flaggedCount > 0 → leave as-is (pending)
  // The user can still override D2's status manually; we only act when
  // the rollup is in a clearly-decided state.
  useEffect(() => {
    if (decisions.length === 0) return
    if (flaggedCount === 0) return
    if (!allFlaggedResolved) return

    const d2 = decisions.find((d) => d.id === 'G2-D2')
    if (!d2) return

    const hasMutations =
      resolutionCounts.recategorized > 0 || resolutionCounts.rejected > 0
    const nextStatus: GateDecisionStatus = hasMutations ? 'edited' : 'approved'
    const nextAnswer = hasMutations
      ? rolledUpAnswerLines.join('\n')
      : d2.answer ?? ''

    // Avoid a render loop: only push when something actually changed.
    if (
      d2.status === nextStatus &&
      (!hasMutations || (d2.answer ?? '') === nextAnswer)
    ) {
      return
    }
    setDecisions(
      decisions.map((d) =>
        d.id === 'G2-D2' ? { ...d, status: nextStatus, answer: nextAnswer } : d,
      ),
    )
    // We intentionally exclude `decisions` / `setDecisions` from the
    // dependency list to avoid re-running on every keystroke in another
    // textarea. The rollup only needs to fire when the resolutions
    // change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allFlaggedResolved,
    flaggedCount,
    resolutionCounts.recategorized,
    resolutionCounts.rejected,
    rolledUpAnswerLines,
  ])

  // Bound resolution helpers — passed down to <ConstraintRow> so each
  // expanded panel can mutate its own constraint without prop-drilling
  // the project/run ids.
  const onSetResolution = (constraintId: string, resolution: ConstraintResolution) => {
    if (resolution.status === 'unreviewed') {
      clearResolution(constraintResolutionKey(project.id, runId, constraintId))
    } else {
      setResolution(
        constraintResolutionKey(project.id, runId, constraintId),
        resolution,
      )
    }
  }

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const expandAllFlagged = () =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      flaggedIds.forEach((id) => next.add(id))
      return next
    })

  const collapseAll = () => setExpandedIds(new Set())

  // Strip the YAML frontmatter off the document-map.md so ReactMarkdown
  // doesn't render it as a `<hr>`-bracketed key/value block. The Observer
  // sometimes emits a *stub* — a ```markdown fence wrapping only the
  // `document_type: document_map` frontmatter (words-only projects with
  // no uploaded artifacts to classify). Strip code fences + frontmatter
  // + bare `key: value` lines so a contentless stub reads as empty (the
  // section then shows a clean empty-state instead of a meaningless
  // rendered code block).
  const documentMapBody = useMemo(() => {
    if (!documentMap) return ''
    const noFences = documentMap.replace(/```[a-zA-Z0-9]*\n?/g, '')
    const noFm = stripFrontmatter(noFences.trim())
    return noFm.replace(/^[a-z_]+:\s*\S+\s*$/gim, '').trim()
  }, [documentMap])

  // Pull the artifact_count out of the document-map frontmatter, with
  // a defensive fallback to "len(constraints) > 0 → at least 1".
  const artifactCount = useMemo(() => {
    const m = documentMap.match(/^artifact_count:\s*(\d+)/m)
    if (m) return Number(m[1])
    return constraints.length > 0 ? null : 0
  }, [documentMap, constraints.length])

  // The distinct artifacts the constraints were extracted FROM, so the
  // architect sees where they came from instead of an abstract (and,
  // for a words-only project, false) "your uploaded T3 artifacts". A
  // charter-derived source is one the pipeline authored itself
  // (phase1/ charter, intent map, problem statement); anything else is
  // an uploaded artifact.
  const sourceArtifacts = useMemo(() => {
    const seen = new Set<string>()
    const out: string[] = []
    for (const c of constraints) {
      if (c.source_artifact && !seen.has(c.source_artifact)) {
        seen.add(c.source_artifact)
        out.push(c.source_artifact)
      }
    }
    return out
  }, [constraints])
  const uploadedSources = useMemo(
    () => sourceArtifacts.filter((s) => !isCharterSource(s)),
    [sourceArtifacts],
  )
  const sourceSummary = useMemo(() => {
    const labels = sourceArtifacts.map(humanizeSource)
    if (labels.length === 0) return ''
    if (labels.length <= 3) return labels.join(', ')
    return `${labels.slice(0, 3).join(', ')} +${labels.length - 3} more`
  }, [sourceArtifacts])

  // Tailor the sub-context shown under each decision so the architect
  // sees exactly what they're being asked about.
  const contextFor = (d: GateDecisionRecord): ReactElement | null => {
    const q = (d.question ?? '').toLowerCase()
    if (/constraint count|major gaps|duplicates/.test(q) || d.id === 'G2-D1') {
      return (
        <DecisionContext label="What you're approving">
          <p>
            <strong className="text-slate-100">{constraints.length}</strong> constraint
            {constraints.length === 1 ? '' : 's'}{' '}
            {constraints.length === 0 ? (
              <>were extracted. The constraint log is empty — see the Document Map below for the Observer's explanation.</>
            ) : uploadedSources.length > 0 ? (
              <>
                were extracted from your{' '}
                <strong className="text-slate-100">{uploadedSources.length}</strong>{' '}
                uploaded artifact{uploadedSources.length === 1 ? '' : 's'}
                {sourceSummary && <> ({sourceSummary})</>}. Skim the table
                above and confirm nothing obvious is missing or duplicated.
              </>
            ) : (
              <>
                were extracted from your project inputs
                {sourceSummary && <> (<strong className="text-slate-100">{sourceSummary}</strong>)</>}
                {' '}— no artifacts were uploaded, so these were mined from
                your problem statement and charter. Skim the table above and
                confirm nothing obvious is missing or duplicated.
              </>
            )}
          </p>
        </DecisionContext>
      )
    }
    if (/human-review|low confidence|requires_human_review|confidence levels/.test(q) || d.id === 'G2-D2') {
      return (
        <DecisionContext label="What you're approving">
          {flaggedCount > 0 ? (
            <p>
              <strong className="text-amber-300">{flaggedCount}</strong> constraint
              {flaggedCount === 1 ? ' is' : 's are'} flagged{' '}
              <code className="rounded bg-slate-800/80 px-1 py-0.5 text-[11px] text-indigo-300">requires_human_review</code>. Those rows are highlighted
              amber in the table above — open each one and confirm the excerpt + category make sense
              before approving.
            </p>
          ) : constraints.length === 0 ? (
            <p>
              No constraints were extracted, so nothing was flagged. Approving here just confirms you
              accept the empty Constraint Log (Phase 3 will run with no extracted constraints).
            </p>
          ) : (
            <p>
              No constraints were flagged for human review — every entry passed the Observer's
              confidence threshold. Approving here means you trust those automatic confidence scores.
            </p>
          )}
        </DecisionContext>
      )
    }
    if (/document map|classification|miscategorised|miscategorized|source material/.test(q) || d.id === 'G2-D3') {
      return (
        <DecisionContext label="What you're approving">
          {documentMapBody.length > 0 ? (
            <p>
              The Document Map (rendered below) classifies each uploaded artifact as narrative,
              visual-architecture, visual-UX, schema, or regulatory. Approve if every classification
              looks right; choose <strong>Edit</strong> to call out specific miscategorisations in
              free-text, or <strong>Reject</strong> if the map is unusable.
            </p>
          ) : (
            <p>
              No artifacts were uploaded for this run, so the Observer produced no Document Map to
              classify (constraints were mined from your problem statement and charter instead).
              Approving here just confirms you're proceeding without one.
            </p>
          )}
        </DecisionContext>
      )
    }
    return null
  }

  return (
    <div className="alm-fadein">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-warn"><AlertCircle className="h-3 w-3" /> Needs you</span>
            <span className="alm-chip">Gate G2</span>
            {decisions.length > 0 && <span className="alm-chip alm-chip-info">{decisions.length} decisions</span>}
            {gate.data?.status && <span className="alm-chip">{gate.data.status}</span>}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">Review Observer Constraints</h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Confirm the extracted constraints and document map before the Generation Swarm consumes them.
          </p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button type="button" onClick={() => emit.mutate()} disabled={emit.isPending} className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5 disabled:opacity-50">
            {emit.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Re-emit
          </button>
          {gate.data?.artifact_path && (
            <a
              href={projectFileUrl(project.id, gate.data.artifact_path)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2.5 py-1 text-xs text-slate-400 hover:bg-white/5"
            >
              <FileText className="h-3 w-3" /> Raw artifact
            </a>
          )}
        </div>
      </div>

      {missing && (
        <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-4 mb-5 text-sm text-slate-300">
          <p className="font-semibold">No gate yet</p>
          <p className="text-xs text-slate-400 mt-1">Click <strong>Re-emit</strong> above to build the G2 artifact.</p>
        </div>
      )}

      {gate.isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading gate…
        </div>
      )}

      {/* Observer-output summary banner — always visible so the architect
          knows at a glance what Phase 2 produced. When the Confidence
          Hardening v1 pipeline produced grounding/verifier signals we
          render them as a second strip immediately below; this lets the
          architect see *why* a row is flagged at a glance instead of
          inferring from the lone "flagged for review" tile. */}
      {(constraintsLoaded || documentMapLoaded) && !missing && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
            <SummaryStat
              icon={<FileText className="h-3.5 w-3.5" />}
              label="Constraints extracted"
              value={String(constraints.length)}
              tone={constraints.length === 0 ? 'warn' : 'info'}
            />
            <SummaryStat
              icon={<ShieldAlert className="h-3.5 w-3.5" />}
              label="Flagged for review"
              value={String(flaggedCount)}
              tone={flaggedCount > 0 ? 'warn' : 'ok'}
            />
            <SummaryStat
              icon={<MapIcon className="h-3.5 w-3.5" />}
              label="T3 artifacts mapped"
              value={artifactCount === null ? '—' : String(artifactCount)}
              tone={artifactCount === 0 ? 'warn' : 'info'}
            />
          </div>
          {/* Confidence Hardening v2 — adjudicator verdict tiles. We
              render this row IN ADDITION to the per-signal v1 row
              below, not instead of it: the architect benefits from
              both the high-level verdict ("4 need review") AND the
              underlying signal counts ("of which 1 is ungrounded, 3
              are category mismatches"). When the project ran with
              the v1 pipeline only (no review_verdict on disk), this
              row is suppressed. */}
          {verdictsAvailable && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
              <SummaryStat
                icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                label="Auto-approved"
                value={String(verdictCounts.auto)}
                tone={verdictCounts.auto > 0 ? 'ok' : 'info'}
              />
              <SummaryStat
                icon={<ShieldAlert className="h-3.5 w-3.5" />}
                label="Needs review"
                value={String(verdictCounts.needsReview)}
                tone={verdictCounts.needsReview > 0 ? 'warn' : 'ok'}
              />
              <SummaryStat
                icon={<AlertCircle className="h-3.5 w-3.5" />}
                label="Uncertain"
                value={String(verdictCounts.uncertain)}
                tone={verdictCounts.uncertain > 0 ? 'warn' : 'ok'}
              />
            </div>
          )}
          {verdictsAvailable && verdictCounts.needsReview > 0 && (
            <div className="text-[11px] text-slate-400 mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-slate-500 uppercase tracking-wider text-[10px]">
                Why a review is needed:
              </span>
              {Object.entries(verdictCounts.reasonBuckets)
                .sort((a, b) => b[1] - a[1])
                .map(([reason, n]) => (
                  <span
                    key={reason}
                    className="inline-flex items-center gap-1 rounded border border-amber-700/40 bg-amber-950/20 px-1.5 py-0.5 text-amber-300"
                    title={reason}
                  >
                    {reviewReasonLabel(reason)} <strong>×{n}</strong>
                  </span>
                ))}
            </div>
          )}
          {evidenceEnabled && (

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
              <SummaryStat
                icon={<span className="text-[11px]">🤖</span>}
                label="Low LLM self-score"
                value={String(lowSelfScoreCount)}
                tone={lowSelfScoreCount > 0 ? 'warn' : 'ok'}
              />
              <SummaryStat
                icon={<span className="text-[11px]">🔍</span>}
                label="Ungrounded excerpts"
                value={String(ungroundedCount)}
                tone={ungroundedCount > 0 ? 'warn' : 'ok'}
              />
              <SummaryStat
                icon={<span className="text-[11px]">⚖️</span>}
                label="Verifier disagreement"
                value={String(verifierDisagreementCount)}
                tone={verifierDisagreementCount > 0 ? 'warn' : 'ok'}
              />
            </div>
          )}
          {!evidenceEnabled && <div className="mb-5" />}
        </>
      )}

      {/* Constraint Log — table OR empty state. Both branches always
          render under a labelled section so the architect can never
          mistake "no UI" for "no data". */}
      {constraintsLoaded && (
        <div className="mb-6">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
            <FileText className="h-3 w-3" /> Constraint Log
            {constraints.length > 0 && (
              <span className="text-slate-600">· {constraints.length} entr{constraints.length === 1 ? 'y' : 'ies'}</span>
            )}
          </div>
          {/* Where the constraints came from — grounds the review so the
              architect isn't guessing whether these are upload-derived or
              charter-mined. */}
          {sourceArtifacts.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
              <span className="text-slate-500">Extracted from:</span>
              {sourceArtifacts.map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1 rounded border border-slate-700/60 bg-slate-900/60 px-1.5 py-0.5 text-slate-300"
                  title={s}
                >
                  {isCharterSource(s) ? (
                    <FileText className="h-3 w-3 text-slate-500" />
                  ) : (
                    <Link2 className="h-3 w-3 text-indigo-400" />
                  )}
                  {humanizeSource(s)}
                </span>
              ))}
              {uploadedSources.length === 0 && (
                <span className="text-slate-500">
                  (mined from your problem statement &amp; charter — no
                  artifacts uploaded)
                </span>
              )}
            </div>
          )}
          {constraints.length > 0 ? (
            <>
              {flaggedCount > 0 && (
                <div className="flex items-center gap-2 mb-2 text-[11px] text-slate-400">
                  <span>Click any row to inspect the full extraction.</span>
                  <span className="text-slate-600">·</span>
                  <button
                    type="button"
                    onClick={expandAllFlagged}
                    className="inline-flex items-center gap-1 rounded border border-amber-700/40 bg-amber-950/20 px-2 py-0.5 text-amber-300 hover:bg-amber-950/40"
                  >
                    <ShieldAlert className="h-3 w-3" /> Expand {flaggedCount} flagged
                  </button>
                  {expandedIds.size > 0 && (
                    <button
                      type="button"
                      onClick={collapseAll}
                      className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-0.5 text-slate-400 hover:bg-white/5"
                    >
                      Collapse all
                    </button>
                  )}
                </div>
              )}
              <div className="rounded-md border border-slate-700/60 bg-slate-900/60 overflow-hidden">
                <table className="w-full text-sm text-left text-slate-300">
                  <thead className="text-xs text-slate-400 bg-slate-800/50 uppercase">
                    <tr>
                      <th className="w-8 px-2 py-2 font-medium" aria-label="Expand"></th>
                      <th className="px-4 py-2 font-medium">ID</th>
                      <th className="px-4 py-2 font-medium">Category</th>
                      <th className="px-4 py-2 font-medium">Confidence</th>
                      <th className="px-4 py-2 font-medium">Source</th>
                      <th className="px-4 py-2 font-medium">Excerpt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50">
                    {sortedConstraints.map(c => {
                      const isOpen = expandedIds.has(c.id)
                      const resolution = resolutions.get(c.id) ?? { status: 'unreviewed' as ConstraintStatus }
                      return (
                        <ConstraintRow
                          key={c.id}
                          constraint={c}
                          isOpen={isOpen}
                          onToggle={() => toggleExpanded(c.id)}
                          resolution={resolution}
                          onResolve={(next) => onSetResolution(c.id, next)}
                        />
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-slate-700/60 border-dashed bg-slate-900/40 p-4 flex items-start gap-3">
              <Inbox className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
              <div className="text-sm text-slate-300">
                <p className="font-medium text-slate-100">No constraints were extracted</p>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  The Observer found no constraints to extract — typically because no T3 artifacts
                  (regulatory PDFs, schemas, narrative briefs, etc.) were uploaded. The Document Map
                  below explains what the Observer saw. You can still approve this gate to proceed
                  with an empty Constraint Log, or rewind to upload more artifacts and re-run Phase 2.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Document Map — rendered as Markdown so the architect can
          actually evaluate the classification before approving G2-D3.
          Previously this string was fetched but never displayed. */}
      {documentMapLoaded && (
        <div className="mb-6">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
            <MapIcon className="h-3 w-3" /> Document Map
          </div>
          {documentMapBody.trim().length > 0 ? (
            <div className="rounded-md border border-slate-700/60 bg-slate-900/60 p-4 max-h-96 overflow-y-auto">
              <div
                className="prose prose-sm dark:prose-invert max-w-none
                           prose-headings:text-slate-100 prose-p:text-slate-300
                           prose-li:text-slate-300 prose-strong:text-slate-100
                           prose-code:text-[0.85em] prose-code:text-indigo-300
                           prose-headings:scroll-mt-8"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{documentMapBody}</ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-slate-700/60 border-dashed bg-slate-900/40 p-3 text-xs text-slate-500">
              The Observer didn't write a document map for this run.
            </div>
          )}
        </div>
      )}

      {/* Per-constraint rollup banner — surfaces the architect's
          progress through the flagged constraints and explains how the
          per-row picks map onto the abstract G2-D2 decision below. */}
      {flaggedCount > 0 && (
        <div
          className={`rounded-lg border p-3 mb-4 text-xs ${
            allFlaggedResolved
              ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-200'
              : 'border-amber-700/40 bg-amber-950/15 text-amber-100'
          }`}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2 min-w-0">
              {allFlaggedResolved ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
              ) : (
                <ShieldAlert className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
              )}
              <div>
                <div className="font-semibold text-slate-100">
                  Per-constraint resolutions —{' '}
                  <span
                    className={
                      allFlaggedResolved ? 'text-emerald-300' : 'text-amber-300'
                    }
                  >
                    {resolutionCounts.reviewed}/{resolutionCounts.total}
                  </span>{' '}
                  resolved
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                  {allFlaggedResolved ? (
                    <>
                      Every flagged constraint has been addressed. Your picks have been rolled up
                      into the <code className="text-indigo-300">G2-D2</code> decision below — review
                      the auto-filled answer and submit when you're done.
                    </>
                  ) : (
                    <>
                      {resolutionCounts.remaining} flagged constraint
                      {resolutionCounts.remaining === 1 ? '' : 's'} still need
                      {resolutionCounts.remaining === 1 ? 's' : ''} a Keep / Recategorize / Reject
                      pick. Click any amber row above to address it inline.
                    </>
                  )}
                </p>
                {resolutionCounts.reviewed > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {resolutionCounts.kept > 0 && (
                      <span className="inline-flex items-center gap-1 rounded border border-emerald-700/40 bg-emerald-950/30 px-1.5 py-0.5 text-[10px] text-emerald-300">
                        <Check className="h-3 w-3" /> {resolutionCounts.kept} kept
                      </span>
                    )}
                    {resolutionCounts.recategorized > 0 && (
                      <span className="inline-flex items-center gap-1 rounded border border-sky-700/40 bg-sky-950/30 px-1.5 py-0.5 text-[10px] text-sky-300">
                        <Edit3 className="h-3 w-3" /> {resolutionCounts.recategorized} recategorized
                      </span>
                    )}
                    {resolutionCounts.rejected > 0 && (
                      <span className="inline-flex items-center gap-1 rounded border border-rose-700/40 bg-rose-950/30 px-1.5 py-0.5 text-[10px] text-rose-300">
                        <X className="h-3 w-3" /> {resolutionCounts.rejected} rejected
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 shrink-0">
              {resolutionCounts.remaining > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    flaggedIds.forEach((id) => {
                      const r = resolutions.get(id)
                      if (!r || r.status === 'unreviewed') {
                        onSetResolution(id, { status: 'kept' })
                      }
                    })
                    toast({
                      title: 'Marked remaining as kept',
                      description: `${resolutionCounts.remaining} flagged constraint${
                        resolutionCounts.remaining === 1 ? '' : 's'
                      } resolved.`,
                    })
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-emerald-700/40 bg-emerald-950/30 px-2 py-1 text-[11px] text-emerald-300 hover:bg-emerald-900/40"
                  title="Trust the Observer for every remaining flagged constraint."
                >
                  <Check className="h-3 w-3" /> Keep remaining ({resolutionCounts.remaining})
                </button>
              )}
              {resolutionCounts.reviewed > 0 && (
                <button
                  type="button"
                  onClick={() => clearResolutionsForRun(project.id, runId)}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:bg-white/5"
                  title="Clear every per-constraint pick for this run."
                >
                  <RefreshCw className="h-3 w-3" /> Reset all
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Decisions split into two sections:
            1. The legacy abstract G2-D# questions (constraint count,
               human-review confirmation, document-map classification)
               keep the generic Approve / Edit / Reject affordance.
            2. The Q4 charter-mined candidates (G2-MINED-*) get a
               purpose-built card with three explicit (A)/(B)/(C)
               buttons whose labels match the question text
               verbatim, and a textarea pre-filled with the candidate
               text when (B) is chosen — so the architect never has
               to retype the excerpt or mentally translate "Approve"
               → "(A) accept as-is". */}
      {(() => {
        const generalDecisions = decisions.filter((d) => !isMinedDecision(d))
        const minedDecisions = decisions.filter((d) => isMinedDecision(d))
        const minedDecided = minedDecisions.filter((d) => d.status !== 'pending').length
        return (
          <>
            {generalDecisions.length > 0 && (
              <>
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                  <Check className="h-3 w-3" /> Your decisions
                </div>
                <div className="space-y-3 mb-6">
                  {generalDecisions.map((decision) => (
                    <div
                      key={decision.id}
                      className="alm-card"
                      data-testid="gate-2-row"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <div className="text-[10.5px] text-slate-500">{decision.id}</div>
                          <div className="text-sm text-slate-100 mt-0.5">{decision.question}</div>
                        </div>
                        <div className="flex gap-1.5 shrink-0 flex-wrap">
                          {(['approved', 'edited', 'rejected'] as GateDecisionStatus[]).map((status) => {
                            const testSuffix = status === 'approved' ? 'approve' : status === 'edited' ? 'modify' : 'reject'
                            return (
                              <button
                                key={status}
                                type="button"
                                data-testid={`gate-2-${testSuffix}`}
                                onClick={() => updateDecision(decision.id, { status })}
                                className="alm-decision"
                                data-selected={decision.status === status ? status : undefined}
                              >
                                {status === 'approved' ? <Check className="h-3 w-3" /> : status === 'edited' ? <Edit3 className="h-3 w-3" /> : <X className="h-3 w-3" />}
                                <span>{decisionLabel(status)}</span>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                      {contextFor(decision)}
                      {decision.status === 'edited' && (
                        <textarea
                          data-testid="gate-2-edit-text"
                          value={decision.answer ?? ''}
                          onChange={(e) => updateDecision(decision.id, { answer: e.target.value })}
                          rows={3}
                          placeholder="Describe your edits/corrections (e.g. 'CON-007 is mislabelled as security; should be regulatory')..."
                          className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 mt-2"
                        />
                      )}
                      {decision.status === 'rejected' && (
                        <textarea
                          data-testid="gate-2-rationale"
                          value={decision.rejection_reason ?? ''}
                          onChange={(e) => updateDecision(decision.id, { rejection_reason: e.target.value })}
                          rows={2}
                          placeholder="Why are you rejecting this? (Phase 3 will not run until G2 is re-emitted)"
                          className="w-full bg-slate-950/40 border border-slate-700 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 mt-2"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {minedDecisions.length > 0 && (
              <>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-[10.5px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Pickaxe className="h-3 w-3" /> Charter-mined candidates
                    <span className="text-slate-600">
                      · {minedDecided}/{minedDecisions.length} resolved
                    </span>
                  </div>
                  {minedDecided < minedDecisions.length && (
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          const next = decisions.map((d) => {
                            if (!isMinedDecision(d)) return d
                            if (d.status !== 'pending') return d
                            return { ...d, status: 'approved' as GateDecisionStatus }
                          })
                          setDecisions(next)
                          toast({
                            title: 'Accepted remaining candidates',
                            description: `${minedDecisions.length - minedDecided} mined candidate${
                              minedDecisions.length - minedDecided === 1 ? '' : 's'
                            } accepted as-is.`,
                          })
                        }}
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-700/40 bg-emerald-950/30 px-2 py-1 text-[11px] text-emerald-300 hover:bg-emerald-900/40"
                        title="Accept every remaining mined candidate as-is."
                      >
                        <Check className="h-3 w-3" /> Accept remaining (
                        {minedDecisions.length - minedDecided})
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const next = decisions.map((d) => {
                            if (!isMinedDecision(d)) return d
                            if (d.status !== 'pending') return d
                            return {
                              ...d,
                              status: 'rejected' as GateDecisionStatus,
                              rejection_reason:
                                d.rejection_reason ??
                                'Bulk-rejected by architect (not a real constraint).',
                            }
                          })
                          setDecisions(next)
                          toast({
                            title: 'Rejected remaining candidates',
                            description: `${minedDecisions.length - minedDecided} mined candidate${
                              minedDecisions.length - minedDecided === 1 ? '' : 's'
                            } dropped.`,
                          })
                        }}
                        className="inline-flex items-center gap-1 rounded-md border border-rose-700/40 bg-rose-950/30 px-2 py-1 text-[11px] text-rose-300 hover:bg-rose-900/40"
                        title="Reject every remaining mined candidate."
                      >
                        <X className="h-3 w-3" /> Reject remaining
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mb-3 leading-relaxed">
                  The deterministic charter miner extracted these candidates from your
                  charter / problem statement / intent map, but their confidence
                  (0.6–0.9) wasn't high enough to auto-promote. Pick (A) accept-as-is,
                  (B) edit before accepting, or (C) reject for each row.
                </p>
                <div className="space-y-3 mb-6">
                  {minedDecisions.map((decision) => (
                    <MinedCandidateCard
                      key={decision.id}
                      decision={decision}
                      onUpdate={(patch) => updateDecision(decision.id, patch)}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )
      })()}

      {gate.data?.status === 'approved' && (
        <div className="rounded-lg p-4 mb-5 border border-emerald-700/40 bg-emerald-950/20 flex items-center gap-2 text-emerald-300 text-sm">
          <CheckCircle2 className="h-4 w-4" /> G2 approved — continue to the next step
        </div>
      )}

      {/* Why-this-matters explainer — mirrors the G1 pattern so the
          architect understands the cost of getting this gate wrong. */}
      <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 mt-1 mb-5 flex gap-2 text-xs text-slate-400">
        <Info className="h-4 w-4 text-indigo-400 shrink-0 mt-0.5" />
        <div>
          <strong className="text-slate-200">Why this gate exists:</strong> the Generation Swarm
          treats the Constraint Log as gospel. A misclassified or missed constraint here propagates
          into BR/SR/Compliance drafts and only surfaces at G4 — costing a Phase-3 rerun. Spend the
          minute now.
        </div>
      </div>

      {/* Primary actions. A gate advances to the NEXT STEP — which is
          G2.5 (Compliance Triage) when the project has compliance cues,
          otherwise the Phase 3 run step. It never runs a phase itself;
          phases run from the Pipeline Runner once their turn comes. */}
      <div className="flex flex-wrap items-center gap-2">
        {gateCleared ? (
          <button
            type="button"
            onClick={() => onApproved?.()}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 hover:from-indigo-400 hover:to-indigo-500"
            title={
              nextIsGate25
                ? 'G2.5 Compliance Triage is next — triage injected stubs before the swarm runs.'
                : undefined
            }
          >
            <CheckCircle2 className="h-4 w-4" />{' '}
            {nextIsGate25 ? 'Continue to G2.5 · Compliance Triage' : 'Continue'}
          </button>
        ) : (

          <>
            <AutoApproveAllButton
              decisions={decisions}
              setDecisions={setDecisions}
              gateName="G2"
              disabled={submit.isPending}
            />
            <button
              type="button"
              data-testid="gate-2-submit"
              onClick={() => submit.mutate()}
              disabled={!allDecided || submit.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-b from-indigo-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-indigo-500"
              title={!allDecided ? 'Choose Approved / Edited / Rejected for every decision first.' : undefined}
            >
              {submit.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <><Check className="h-4 w-4" /> Approve & continue</>}
            </button>
          </>
        )}
        {!allDecided && !gateCleared && decisions.length > 0 && (
          <span className="text-xs text-slate-500">
            Decide every item first.
          </span>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------- //
// Helpers
// ---------------------------------------------------------------------- //

/** Compact stat tile for the Observer-output summary banner. */
function SummaryStat({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactNode
  label: string
  value: string
  tone: 'ok' | 'warn' | 'info'
}) {
  const toneClass =
    tone === 'warn'
      ? 'border-amber-700/40 bg-amber-950/20'
      : tone === 'ok'
      ? 'border-emerald-700/40 bg-emerald-950/15'
      : 'border-slate-700/60 bg-slate-900/40'
  return (
    <div className={`rounded-md border ${toneClass} px-3 py-2`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-lg font-semibold text-slate-100 mt-0.5">{value}</div>
    </div>
  )
}

/** Inline context box rendered above the Edit/Reject textareas so the
 *  architect always sees what each decision is anchored to. */
function DecisionContext({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="rounded-md border border-slate-700/50 bg-slate-950/40 p-3 mt-2 mb-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
      <div className="text-[12.5px] leading-relaxed text-slate-300 [&>p]:m-0">{children}</div>
    </div>
  )
}

/** Single constraint row, with an inline-expanded detail panel.
 *
 *  Previously the table truncated the excerpt + source to ~200/120 px
 *  with no way to actually inspect a flagged row. The G2-D2 prompt
 *  literally asks the architect to "open each one and confirm the
 *  excerpt + category make sense", so we now render every row as a
 *  click-to-expand affordance and a second `<tr>` containing every
 *  field on the underlying `ConstraintRecord` (including extraction
 *  method and any linked requirements). */
function ConstraintRow({
  constraint,
  isOpen,
  onToggle,
  resolution,
  onResolve,
}: {
  constraint: ConstraintRecord
  isOpen: boolean
  onToggle: () => void
  resolution: ConstraintResolution
  onResolve: (next: ConstraintResolution) => void
}) {
  const c = constraint
  const flagged = c.requires_human_review
  const lowConfidence = c.confidence < 0.7
  // Tone the row by resolution status when the architect has acted on
  // it: green/red overrides the amber "needs review" tint so they get a
  // visual confirmation their pick was recorded. Untouched flagged rows
  // stay amber until the architect addresses them.
  const isReviewed = resolution.status !== 'unreviewed'
  const baseTone =
    isReviewed && resolution.status === 'kept'
      ? 'bg-emerald-950/15'
      : isReviewed && resolution.status === 'recategorized'
      ? 'bg-sky-950/15'
      : isReviewed && resolution.status === 'rejected'
      ? 'bg-rose-950/15'
      : flagged
      ? 'bg-amber-950/20'
      : ''
  const hoverTone =
    isReviewed && resolution.status === 'kept'
      ? 'hover:bg-emerald-950/25'
      : isReviewed && resolution.status === 'recategorized'
      ? 'hover:bg-sky-950/25'
      : isReviewed && resolution.status === 'rejected'
      ? 'hover:bg-rose-950/25'
      : flagged
      ? 'hover:bg-amber-950/30'
      : 'hover:bg-slate-800/30'
  return (
    <>
      <tr
        onClick={onToggle}
        className={`${baseTone} ${hoverTone} cursor-pointer select-none`}
        aria-expanded={isOpen}
      >
        <td className="w-8 px-2 py-2 text-slate-500">
          {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </td>
        <td className="px-4 py-2 font-mono text-[11px] text-slate-400">
          <div className="flex flex-wrap items-center gap-1.5">
            {c.id}
            {flagged && !isReviewed && (
              <span title="Requires human review">
                <ShieldAlert className="h-3 w-3 text-amber-400" />
              </span>
            )}
            {/* Confidence Hardening v2 — show the adjudicator's
                primary reason inline so the architect sees *why* the
                row is amber without expanding it. Only render when
                the row is unreviewed AND has a reason; once the
                architect resolves it the ResolutionPill takes over
                and the reason is moot. */}
            {flagged && !isReviewed && c.review_primary_reason && (
              <span
                className="inline-flex items-center rounded border border-amber-700/40 bg-amber-950/30 px-1 py-0.5 text-[9.5px] font-medium text-amber-300"
                title={
                  c.review_reasons?.find(
                    (r) => r.signal && !r.ok && r.note,
                  )?.note ?? reviewReasonLabel(c.review_primary_reason)
                }
              >
                {reviewReasonLabel(c.review_primary_reason)}
              </span>
            )}
            {/* When the adjudicator says "auto_approve", surface a
                tiny green tick on unflagged rows that ran through v2
                so the architect can tell the row was actively
                cleared (not just "not flagged"). */}
            {!flagged && c.review_verdict === 'auto_approve' && (
              <span
                title="All adjudicator signals passed for this row."
                className="inline-flex items-center"
              >
                <CheckCircle2 className="h-3 w-3 text-emerald-400/80" />
              </span>
            )}
            {isReviewed && <ResolutionPill resolution={resolution} />}
          </div>
        </td>

        <td className="px-4 py-2">
          <span className="alm-chip">
            {resolution.status === 'recategorized' && resolution.newCategory
              ? resolution.newCategory
              : c.category}
          </span>
          {resolution.status === 'recategorized' && resolution.newCategory && (
            <span className="ml-1 text-[10px] text-slate-500 line-through">{c.category}</span>
          )}
        </td>
        <td className="px-4 py-2">
          <span className={`alm-chip ${flagged || lowConfidence ? 'alm-chip-warn' : 'alm-chip-ok'}`}>
            {Math.round(c.confidence * 100)}%
          </span>
        </td>
        <td
          className="px-4 py-2 text-[11px] text-slate-400 truncate max-w-[120px]"
          title={c.source_artifact}
        >
          {c.source_artifact}
        </td>
        <td className="px-4 py-2 text-xs truncate max-w-[200px]" title={c.excerpt}>
          {c.excerpt}
        </td>
      </tr>
      {isOpen && (
        <tr className={baseTone}>
          <td></td>
          <td colSpan={5} className="px-4 pb-4 pt-1">
            <div className="rounded-md border border-slate-700/60 bg-slate-950/50 p-3 space-y-3 text-xs text-slate-300">
              <DetailField label="Excerpt">
                <p className="whitespace-pre-wrap leading-relaxed text-slate-200">{c.excerpt}</p>
              </DetailField>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <DetailField label="Source artifact">
                  <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[11px] text-indigo-300">
                    {c.source_artifact}
                  </code>
                </DetailField>
                <DetailField label="Extraction method">
                  <code className="rounded bg-slate-800/80 px-1.5 py-0.5 text-[11px] text-indigo-300">
                    {c.extraction_method}
                  </code>
                </DetailField>
                <DetailField label="Category">
                  <span className="alm-chip">{c.category}</span>
                  {c.verifier_category_suggestion && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[11px] text-amber-300">
                      Verifier suggests <code className="rounded bg-slate-800/80 px-1 py-0.5 text-indigo-300">{c.verifier_category_suggestion}</code>
                    </span>
                  )}
                </DetailField>
                <DetailField label="Linked requirements">
                  {c.linked_requirements && c.linked_requirements.length > 0 ? (
                    <ul className="list-none space-y-1">
                      {c.linked_requirements.map((r, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-slate-300">
                          <Link2 className="h-3 w-3 mt-0.5 text-slate-500 shrink-0" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-[11px] text-slate-500">none</span>
                  )}
                </DetailField>
                {/* Option C (2026-05-19): per-regulation traceability.
                    A charter-mined constraint whose excerpt mentions
                    multiple standards gets a chip per standard so the
                    architect can see at a glance which standards are
                    in scope without re-parsing the excerpt. */}
                {c.regulatory_tags && c.regulatory_tags.length > 0 && (
                  <DetailField label="Regulations">
                    <RegulatoryTagChips tags={c.regulatory_tags} />
                  </DetailField>
                )}
              </div>

              {/* Confidence Hardening v1 — 3-up Evidence panel.
                  Each tile is independent: a row can be 🟢 grounded but
                  🔴 verifier-disagrees (the architect should see both).
                  When neither the grounder nor verifier ran (older logs),
                  we still render the self-score tile so the panel is never
                  empty. */}
              <EvidencePanel constraint={c} flagged={flagged} lowConfidence={lowConfidence} />

              {/* NEW: Per-constraint resolution panel. Lets the
                  architect address each flagged row individually
                  (Keep / Recategorize / Reject) instead of having to
                  funnel everything through the abstract G2-D2 textarea
                  at the bottom. The picks are persisted and rolled up
                  into G2-D2's status + answer text automatically. */}
              {flagged && (
                <ConstraintResolutionPanel
                  constraint={c}
                  resolution={resolution}
                  onResolve={onResolve}
                />
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

/** Tiny inline pill that mirrors the architect's per-constraint pick.
 *  Shown next to the constraint id in the table row so a reviewed row
 *  is recognisable at a glance after the panel is collapsed. */
function ResolutionPill({ resolution }: { resolution: ConstraintResolution }) {
  if (resolution.status === 'kept') {
    return (
      <span
        className="inline-flex items-center gap-0.5 rounded border border-emerald-700/40 bg-emerald-950/30 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300"
        title="You marked this constraint as correct as-is."
      >
        <Check className="h-2.5 w-2.5" /> kept
      </span>
    )
  }
  if (resolution.status === 'recategorized') {
    return (
      <span
        className="inline-flex items-center gap-0.5 rounded border border-sky-700/40 bg-sky-950/30 px-1.5 py-0.5 text-[10px] font-medium text-sky-300"
        title={`You changed the category to ${resolution.newCategory ?? '?'}.`}
      >
        <Edit3 className="h-2.5 w-2.5" /> recat → {resolution.newCategory ?? '?'}
      </span>
    )
  }
  if (resolution.status === 'rejected') {
    return (
      <span
        className="inline-flex items-center gap-0.5 rounded border border-rose-700/40 bg-rose-950/30 px-1.5 py-0.5 text-[10px] font-medium text-rose-300"
        title="You marked this constraint as not a real constraint."
      >
        <X className="h-2.5 w-2.5" /> rejected
      </span>
    )
  }
  return null
}

/** The actionable resolution panel rendered inside an expanded
 *  flagged constraint row. Three primary actions:
 *
 *    Keep         — Architect confirms the extraction is correct as-is.
 *                   Rolls up to "approved" on G2-D2 if every flagged
 *                   row gets the same treatment.
 *    Recategorize — Architect picks a new category (the verifier's
 *                   suggestion is one tap away). Rolls up to "edited".
 *    Reject       — Architect says this isn't a real constraint and
 *                   should be dropped from the log. Rolls up to
 *                   "edited".
 *
 *  An optional note field lets the architect pin a one-line
 *  justification that flows verbatim into the G2-D2 answer text. */
function ConstraintResolutionPanel({
  constraint,
  resolution,
  onResolve,
}: {
  constraint: ConstraintRecord
  resolution: ConstraintResolution
  onResolve: (next: ConstraintResolution) => void
}) {
  const c = constraint
  const status = resolution.status
  const verifierSuggestion = c.verifier_category_suggestion
  const newCategory = resolution.newCategory ?? verifierSuggestion ?? c.category
  const note = resolution.note ?? ''

  return (
    <div className="rounded-md border border-indigo-700/40 bg-indigo-950/15 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider text-indigo-300/80 font-medium">
          Resolve this constraint
        </div>
        {status !== 'unreviewed' && (
          <button
            type="button"
            onClick={() => onResolve({ status: 'unreviewed' })}
            className="text-[10px] text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline"
          >
            Reset
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-2">
        <button
          type="button"
          onClick={() => onResolve({ status: 'kept', note: resolution.note })}
          className="alm-decision"
          data-selected={status === 'kept' ? 'approved' : undefined}
          title="The extraction looks right — keep this constraint as-is."
        >
          <Check className="h-3 w-3" /> Keep as-is
        </button>
        <button
          type="button"
          onClick={() =>
            onResolve({
              status: 'recategorized',
              newCategory: newCategory,
              note: resolution.note,
            })
          }
          className="alm-decision"
          data-selected={status === 'recategorized' ? 'edited' : undefined}
          title="Change the category — pick the right one below."
        >
          <Edit3 className="h-3 w-3" /> Recategorize
        </button>
        <button
          type="button"
          onClick={() => onResolve({ status: 'rejected', note: resolution.note })}
          className="alm-decision"
          data-selected={status === 'rejected' ? 'rejected' : undefined}
          title="This isn't a real constraint — drop it from the log."
        >
          <X className="h-3 w-3" /> Reject
        </button>
      </div>

      {/* Recategorize sub-control — picker (or shortcut tile) for the
          new category. We surface the verifier's suggestion as a
          one-tap chip when present so the most common fix is single
          click. */}
      {status === 'recategorized' && (
        <div className="rounded border border-sky-700/30 bg-sky-950/20 p-2 mb-2 space-y-2">
          {verifierSuggestion && verifierSuggestion !== c.category && (
            <button
              type="button"
              onClick={() =>
                onResolve({
                  status: 'recategorized',
                  newCategory: verifierSuggestion,
                  note: resolution.note,
                })
              }
              className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] border ${
                newCategory === verifierSuggestion
                  ? 'border-sky-500 bg-sky-900/40 text-sky-200'
                  : 'border-sky-700/40 bg-sky-950/30 text-sky-300 hover:bg-sky-900/40'
              }`}
            >
              <Check className="h-3 w-3" /> Use verifier's suggestion: {verifierSuggestion}
            </button>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">
              New category
            </label>
            <select
              value={newCategory}
              onChange={(e) =>
                onResolve({
                  status: 'recategorized',
                  newCategory: e.target.value,
                  note: resolution.note,
                })
              }
              className="bg-slate-950/60 border border-slate-700 rounded px-2 py-0.5 text-[11px] text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
            >
              {CONSTRAINT_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <span className="text-[10px] text-slate-500">
              was{' '}
              <code className="rounded bg-slate-800/80 px-1 py-0.5 text-indigo-300">{c.category}</code>
            </span>
          </div>
        </div>
      )}

      {/* Optional note — flows verbatim into G2-D2.answer. */}
      {status !== 'unreviewed' && (
        <input
          type="text"
          value={note}
          onChange={(e) =>
            onResolve({
              ...resolution,
              note: e.target.value,
            })
          }
          placeholder={
            status === 'kept'
              ? "Optional: why you're keeping this (e.g. 'verifier was wrong, rule lives in §3.2')..."
              : status === 'recategorized'
              ? "Optional: short justification for the new category..."
              : "Optional: why this isn't a real constraint..."
          }
          className="w-full bg-slate-950/40 border border-slate-700 rounded px-2 py-1 text-[11px] text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500/50"
        />
      )}

      {status === 'unreviewed' && (
        <p className="text-[11px] text-slate-400 leading-relaxed">
          The Observer's confidence in this extraction is below the auto-accept threshold. Pick{' '}
          <strong>Keep as-is</strong> if the excerpt + category look right,{' '}
          <strong>Recategorize</strong> to fix the category{' '}
          {verifierSuggestion ? (
            <>
              (the verifier suggests <code className="text-indigo-300">{verifierSuggestion}</code>)
            </>
          ) : null}
          , or <strong>Reject</strong> if this isn't really a constraint. Your picks roll up into
          the <code className="text-indigo-300">G2-D2</code> decision automatically.
        </p>
      )}
    </div>
  )
}

/** Tiny label/value block used inside the expanded constraint detail. */
function DetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="text-xs text-slate-300">{children}</div>
    </div>
  )
}

// Option C (2026-05-19): map the miner's stable regulatory tag tokens
// (defined in ai_alm/services/charter_constraint_miner.py::_REGULATORY_TAGS)
// to a human-readable chip label. Anything not in the map falls back to a
// title-cased version of the token so a future tag is rendered sanely
// without a frontend release.
const REGULATORY_TAG_LABELS: Record<string, string> = {
  iso_27001: 'ISO 27001',
  soc_2: 'SOC 2',
  hipaa: 'HIPAA',
  gdpr: 'GDPR',
  pci_dss: 'PCI-DSS',
  wcag: 'WCAG',
  owasp_asvs: 'OWASP ASVS',
  fda_510k: 'FDA 510(k)',
  section_508: 'Section 508',
  nist_800: 'NIST 800-series',
  fedramp: 'FedRAMP',
  iec_62304: 'IEC 62304',

  // Gap 5 expansion (2026-05-19) — mirror the miner's tag table at
  // src/ai_alm/services/charter_constraint_miner.py::_REGULATORY_TAGS
  // and the intake decomposition at
  // src/ai_alm/services/phase2_runner.py::_REGULATORY_FLAG_DECOMPOSITION.
  // Note: 'fda' is the bare FDA flag (from intake fda_hipaa paired flag);
  // 'fda_510k' above is the specific medical-device clearance pattern.
  sox: 'SOX',
  hitrust: 'HITRUST',
  ccpa_cpra: 'CCPA / CPRA',
  ferpa_coppa: 'FERPA / COPPA',
  glba: 'GLBA',
  itar_ear: 'ITAR / EAR',
  cjis: 'CJIS',
  iso_42001: 'ISO 42001',
  nist_ai_rmf: 'NIST AI RMF',
  fda: 'FDA',
}

function humaniseRegulatoryTag(token: string): string {
  const known = REGULATORY_TAG_LABELS[token]
  if (known) return known
  return token
    .split('_')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ')
}

/** One chip per regulatory tag aggregated onto a charter-mined Constraint.
 *  Sorted by humanised label so the chip order is stable across re-renders
 *  even when the backend's insertion order shifts. */
function RegulatoryTagChips({ tags }: { tags: string[] }) {
  const seen = new Set<string>()
  const ordered = tags
    .filter((t) => {
      if (!t || seen.has(t)) return false
      seen.add(t)
      return true
    })
    .map((t) => ({ token: t, label: humaniseRegulatoryTag(t) }))
    .sort((a, b) => a.label.localeCompare(b.label))
  if (ordered.length === 0) return null
  return (
    <div className="flex flex-wrap gap-1">
      {ordered.map(({ token, label }) => (
        <span
          key={token}
          className="inline-flex items-center rounded border border-indigo-700/40 bg-indigo-950/30 px-1.5 py-0.5 text-[10px] font-medium text-indigo-200"
          title={`Charter-mining tag: ${token}`}
        >
          {label}
        </span>
      ))}
    </div>
  )
}

/** Confidence Hardening v1 — three independent signals laid out side by
 *  side so the architect can tell at a glance which checks the row
 *  passed and which it failed.
 *
 *  Plan reference: ``plan/AI_ALM_Constraint_Confidence_Plan.md`` §8.2.
 *
 *  Each tile uses the same colour vocabulary as the rest of the gate:
 *    🟢 emerald  — passed
 *    🟡 amber    — failed / flagged for review
 *    ⚪ slate    — didn't run for this row (vision-only, source missing,
 *                  legacy log, …)
 */
function EvidencePanel({
  constraint,
  flagged,
  lowConfidence,
}: {
  constraint: ConstraintRecord
  flagged: boolean
  lowConfidence: boolean
}) {
  const c = constraint
  const grounded = c.grounded
  const verifier = c.verifier_passed

  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
        Evidence
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {/* Tile 1 — deterministic grounder */}
        <EvidenceTile
          label="Grounded in source"
          state={
            grounded === true ? 'pass' : grounded === false ? 'fail' : 'na'
          }
          headline={
            grounded === true
              ? c.grounding_method === 'exact'
                ? 'Exact match'
                : `Fuzzy ${c.grounding_score != null ? `${Math.round(c.grounding_score * 100)}%` : 'match'}`
              : grounded === false
              ? 'Excerpt not found'
              : c.grounding_method === 'skipped'
              ? 'Skipped'
              : 'Did not run'
          }
          detail={
            grounded === true
              ? 'The excerpt was located inside the cited source artifact.'
              : grounded === false
              ? "The grounder could not find this excerpt anywhere in the cited source — likely fabricated or mis-attributed."
              : 'The deterministic grounder did not run on this row (vision extraction, non-text source, or feature flag off).'
          }
        />

        {/* Tile 2 — cheap-LLM verifier */}
        <EvidenceTile
          label="Verifier (2nd LLM)"
          state={
            verifier === true ? 'pass' : verifier === false ? 'fail' : 'na'
          }
          headline={
            verifier === true
              ? 'Agrees'
              : verifier === false
              ? c.verifier_category_suggestion
                ? `Suggests ${c.verifier_category_suggestion}`
                : 'Disagrees'
              : 'Did not run'
          }
          detail={
            c.verifier_reasoning
              ? c.verifier_reasoning
              : verifier === true
              ? 'A second LLM independently confirmed the excerpt is faithful and the category is right.'
              : verifier === false
              ? 'The cheap second-LLM critic disagreed on either the excerpt or the category.'
              : 'The verifier did not run for this row (feature flag off, vision-only, or call failed).'
          }
          footer={
            c.verifier_model
              ? `model: ${c.verifier_model}`
              : undefined
          }
        />

        {/* Tile 3 — Observer's self-score (always present) */}
        <EvidenceTile
          label="LLM self-score"
          state={
            flagged || lowConfidence
              ? 'fail'
              : 'pass'
          }
          headline={`${Math.round(c.confidence * 100)}%`}
          detail="Self-reported by the Observer LLM (not a calibrated metric). Anything below 0.70 is auto-flagged for human review."
        />
      </div>
    </div>
  )
}

/** Single tile inside `EvidencePanel`. Tone is driven by `state`:
 *    pass → emerald, fail → amber, na → slate (muted).
 *  Headline is the one-glance verdict; detail is the prose explanation. */
function EvidenceTile({
  label,
  state,
  headline,
  detail,
  footer,
}: {
  label: string
  state: 'pass' | 'fail' | 'na'
  headline: string
  detail: string
  footer?: string
}) {
  const toneClass =
    state === 'pass'
      ? 'border-emerald-700/40 bg-emerald-950/15'
      : state === 'fail'
      ? 'border-amber-700/40 bg-amber-950/20'
      : 'border-slate-700/60 bg-slate-900/30'
  const headlineClass =
    state === 'pass'
      ? 'text-emerald-300'
      : state === 'fail'
      ? 'text-amber-300'
      : 'text-slate-400'
  const icon = state === 'pass' ? '🟢' : state === 'fail' ? '🟡' : '⚪'
  return (
    <div className={`rounded-md border ${toneClass} px-3 py-2 flex flex-col gap-1`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div className={`text-sm font-semibold ${headlineClass}`}>{headline}</div>
      <p className="text-[11px] text-slate-400 leading-relaxed">{detail}</p>
      {footer && (
        <p className="text-[10px] text-slate-500 mt-1">{footer}</p>
      )}
    </div>
  )
}

/** Quality-Uplift Q4 / UAT 2026-05-05 — purpose-built card for
 *  charter-mined candidate ratification rows.
 *
 *  Why the dedicated card instead of the generic Approve/Edit/Reject
 *  affordance shared by every other gate decision: the underlying
 *  question text reads *"Promote this excerpt to a constraint? Choose:
 *  (A) accept as-is, (B) edit text before accepting, or (C) reject
 *  (drop)"*. Mapping that triplet onto the visually neutral
 *  Approve/Edit/Reject buttons forced the architect to mentally
 *  translate every click — and worse, when they picked Edit the
 *  textarea was empty so they had to re-type the entire candidate
 *  excerpt before they could tweak a single word.
 *
 *  This card:
 *    - Renders the *full* candidate text (untruncated), classification,
 *      regulatory tag, source artifact + line range, and miner
 *      confidence so the architect has every input on screen.
 *    - Surfaces three explicit, labelled (A)/(B)/(C) buttons whose
 *      copy matches the question verbatim.
 *    - Maps each button onto the existing GateDecision status enum
 *      (accept_as_is → approved, edit_text → edited, reject_drop →
 *      rejected) so the legacy backend resolver still sees the
 *      same approved/edited/rejected vocabulary.
 *    - Pre-fills the (B) edit textarea with the original candidate
 *      text so the architect can tweak instead of retype.
 */
function MinedCandidateCard({
  decision,
  onUpdate,
}: {
  decision: GateDecisionRecord
  onUpdate: (patch: Partial<GateDecisionRecord>) => void
}) {
  const payload = getMinedPayload(decision)
  // Defensive fallback: if the backend payload is missing (older
  // gate.json on disk), render a minimal card with the verbatim
  // question text so the architect still has working A/B/C buttons.
  const status = decision.status
  const text = payload?.text ?? extractTextFromQuestion(decision.question)
  const confidencePct =
    payload?.confidence != null
      ? `${Math.round(payload.confidence * 100)}%`
      : null
  const sourceLineLabel = (() => {
    const range = payload?.source_line_range
    if (!range || range.length === 0) return null
    if (range.length === 1) return `L${range[0]}`
    if (range[0] === range[1]) return `L${range[0]}`
    return `L${range[0]}–${range[1]}`
  })()

  // Visual tone for the whole card mirrors the architect's pick so a
  // resolved row is immediately recognisable in a long list.
  const cardTone =
    status === 'approved'
      ? 'border-emerald-700/40 bg-emerald-950/10'
      : status === 'edited'
      ? 'border-sky-700/40 bg-sky-950/10'
      : status === 'rejected'
      ? 'border-rose-700/40 bg-rose-950/10'
      : 'border-slate-700/60 bg-slate-900/40'

  const onPickAccept = () =>
    onUpdate({
      status: 'approved',
      answer: 'accept_as_is',
      rejection_reason: null,
    })
  const onPickEdit = () => {
    // Pre-fill the answer with the candidate text so the architect can
    // tweak instead of retype — only when the answer is currently
    // empty/null (preserve any text they already typed).
    const seed =
      decision.answer && decision.answer.trim() && decision.answer !== 'accept_as_is'
        ? decision.answer
        : text
    onUpdate({ status: 'edited', answer: seed, rejection_reason: null })
  }
  const onPickReject = () =>
    onUpdate({
      status: 'rejected',
      answer: 'reject_drop',
      rejection_reason: decision.rejection_reason ?? '',
    })

  return (
    <div className={`alm-card border ${cardTone}`}>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10.5px] text-slate-500 font-mono">
            {decision.id}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            {payload?.classification && (
              <span className="alm-chip">{payload.classification}</span>
            )}
            {payload?.regulatory_tag && (
              <span className="alm-chip alm-chip-info">
                {payload.regulatory_tag}
              </span>
            )}
            {confidencePct && (
              <span
                className="alm-chip"
                title="Charter miner confidence"
              >
                {confidencePct}
              </span>
            )}
            {payload?.source_artifact && (
              <span className="text-[11px] text-slate-500 truncate max-w-[280px]">
                <code className="rounded bg-slate-800/80 px-1 py-0.5 text-indigo-300">
                  {payload.source_artifact}
                  {sourceLineLabel ? `:${sourceLineLabel}` : ''}
                </code>
              </span>
            )}
            {payload?.duplicate_count != null && payload.duplicate_count > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded border border-indigo-700/40 bg-indigo-950/40 px-1.5 py-0.5 text-[10px] text-indigo-300"
                title="The miner found this same sentence in multiple artifacts. Resolving this card resolves every occurrence."
              >
                +{payload.duplicate_count} duplicate
                {payload.duplicate_count === 1 ? '' : 's'}
              </span>
            )}
          </div>
          {/* When the miner produced this same sentence in more than
              one artifact, list every (artifact, line) so the architect
              can still cross-reference the original locations. We
              skip the first source (already rendered above as the
              primary). */}
          {payload?.sources && payload.sources.length > 1 && (
            <div className="mt-1.5 text-[10.5px] text-slate-500 leading-snug">
              <span className="text-slate-600">Also seen in:</span>{' '}
              {payload.sources.slice(1).map((s, i) => {
                const range = s.source_line_range
                let lineLabel = ''
                if (range && range.length > 0) {
                  lineLabel =
                    range.length === 1 || range[0] === range[1]
                      ? `:L${range[0]}`
                      : `:L${range[0]}–${range[1]}`
                }
                return (
                  <span key={`${s.suggested_id ?? i}`}>
                    {i > 0 && ', '}
                    <code className="rounded bg-slate-800/60 px-1 py-0.5 text-indigo-300/80">
                      {s.source_artifact ?? '?'}
                      {lineLabel}
                    </code>
                  </span>
                )
              })}
            </div>
          )}
        </div>
        {status !== 'pending' && (
          <button
            type="button"
            onClick={() =>
              onUpdate({
                status: 'pending',
                answer: null,
                rejection_reason: null,
              })
            }
            className="text-[10px] text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline shrink-0"
          >
            Reset
          </button>
        )}
      </div>

      {/* Candidate text — the actual content the architect is being
          asked to promote. Rendered verbatim (no truncation) so they
          can read it without re-opening source files. */}
      <div className="rounded-md border border-slate-700/50 bg-slate-950/40 p-3 mb-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
          Candidate text
        </div>
        <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
          {text}
        </p>
      </div>

      {/* Explicit (A)/(B)/(C) choice buttons — the entire point of
          this card. Each button's label mirrors the question text so
          the architect sees exactly what the gate is asking. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
        <ChoiceButton
          letter="A"
          label="Accept as-is"
          summary="Promote the excerpt verbatim into the constraint log."
          icon={<Check className="h-3 w-3" />}
          tone="approved"
          selected={status === 'approved'}
          onClick={onPickAccept}
        />
        <ChoiceButton
          letter="B"
          label="Edit text"
          summary="Tweak the wording before promoting."
          icon={<Edit3 className="h-3 w-3" />}
          tone="edited"
          selected={status === 'edited'}
          onClick={onPickEdit}
        />
        <ChoiceButton
          letter="C"
          label="Reject"
          summary="Drop this candidate — not a real constraint."
          icon={<X className="h-3 w-3" />}
          tone="rejected"
          selected={status === 'rejected'}
          onClick={onPickReject}
        />
      </div>

      {/* (B) — pre-filled textarea so the architect can tweak the
          excerpt instead of retyping it from scratch. */}
      {status === 'edited' && (
        <div className="mt-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
            Edited constraint text (will be promoted)
          </label>
          <textarea
            value={decision.answer ?? ''}
            onChange={(e) => onUpdate({ answer: e.target.value })}
            rows={3}
            placeholder="Edit the candidate text before it's promoted..."
            className="w-full bg-slate-950/40 border border-sky-700/40 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500/50"
          />
          {text && decision.answer && decision.answer !== text && (
            <p className="text-[10px] text-slate-500 mt-1">
              Original:{' '}
              <span className="line-through text-slate-600">{text}</span>
            </p>
          )}
        </div>
      )}

      {/* (C) — optional reason. Bulk-reject seeds a default; the
          architect can refine. */}
      {status === 'rejected' && (
        <div className="mt-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
            Why are you rejecting this? (optional)
          </label>
          <textarea
            value={decision.rejection_reason ?? ''}
            onChange={(e) => onUpdate({ rejection_reason: e.target.value })}
            rows={2}
            placeholder="e.g. 'duplicate of C-014', 'commentary not a constraint'..."
            className="w-full bg-slate-950/40 border border-rose-700/40 rounded-md px-3 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-rose-500/50"
          />
        </div>
      )}

      {status === 'pending' && (
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Pick one of (A) / (B) / (C) above to resolve this candidate.
          Phase 3 won't run until every mined candidate has a decision.
        </p>
      )}
    </div>
  )
}

/** A single labelled (A)/(B)/(C) choice button used by the
 *  ``MinedCandidateCard``. Visually distinct (large, two-line, tinted
 *  by tone) so the three options read as a clear poll-style picker
 *  rather than the dense Approve/Edit/Reject button row used by
 *  legacy decisions. */
function ChoiceButton({
  letter,
  label,
  summary,
  icon,
  tone,
  selected,
  onClick,
}: {
  letter: 'A' | 'B' | 'C'
  label: string
  summary: string
  icon: ReactNode
  tone: 'approved' | 'edited' | 'rejected'
  selected: boolean
  onClick: () => void
}) {
  const baseTone = (() => {
    if (selected) {
      if (tone === 'approved')
        return 'border-emerald-500 bg-emerald-900/30 text-emerald-100 ring-1 ring-emerald-500/40'
      if (tone === 'edited')
        return 'border-sky-500 bg-sky-900/30 text-sky-100 ring-1 ring-sky-500/40'
      return 'border-rose-500 bg-rose-900/30 text-rose-100 ring-1 ring-rose-500/40'
    }
    return 'border-slate-700 bg-slate-950/40 text-slate-300 hover:bg-slate-800/60'
  })()
  const letterTone = (() => {
    if (selected) {
      if (tone === 'approved') return 'bg-emerald-700/60 text-emerald-50'
      if (tone === 'edited') return 'bg-sky-700/60 text-sky-50'
      return 'bg-rose-700/60 text-rose-50'
    }
    if (tone === 'approved') return 'bg-emerald-950/60 text-emerald-300'
    if (tone === 'edited') return 'bg-sky-950/60 text-sky-300'
    return 'bg-rose-950/60 text-rose-300'
  })()
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-start gap-2 rounded-md border px-3 py-2 text-left transition ${baseTone}`}
      data-selected={selected ? 'true' : undefined}
    >
      <span
        className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold ${letterTone}`}
      >
        {letter}
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1 text-xs font-semibold">
          {icon}
          {label}
        </span>
        <span className="block text-[11px] text-slate-400 mt-0.5 leading-snug">
          {summary}
        </span>
      </span>
    </button>
  )
}

/** Defensive helper for older gates whose decisions arrived without a
 *  structured payload — pulls the candidate excerpt out of the
 *  backend's question text (`Promote *…* to a constraint?`). */
function extractTextFromQuestion(question: string): string {
  const m = (question ?? '').match(/Promote \*([^*]+?)\* to a constraint/i)
  if (m) return m[1].trim()
  return ''
}
